import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Dialog } from '@headlessui/react';
import { X, ZoomIn, ZoomOut, RotateCcw, Download, Share2 } from 'lucide-react';
import { getDisplayUrl } from '../lib/r2Storage';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
}

export default function ImageModal({ isOpen, onClose, imageUrl }: ImageModalProps) {
  // Route the image through the storage proxy (unblocked + handles legacy r2.dev URLs).
  const displayUrl = getDisplayUrl(imageUrl);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
      setIsLoading(true);
      setImageError(false);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  const handleZoomIn = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setScale(prev => Math.min(prev * 1.5, 5));
  };

  const handleZoomOut = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setScale(prev => Math.max(prev / 1.5, 0.25));
  };

  const handleReset = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const response = await fetch(displayUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `image-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Image',
          url: displayUrl,
        });
      } catch (error) {
        console.error('Share failed:', error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(displayUrl);
      } catch (error) {
        console.error('Copy failed:', error);
      }
    }
  };

  const handlePointerStart = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handlePointerEnd = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const handleImageDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (scale === 1) {
      setScale(2);
    } else {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setImageError(true);
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      className="relative z-[9999]"
      static
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/95 backdrop-blur-sm transition-opacity duration-300"
        aria-hidden="true"
      />

      {/* Modal container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel 
          className="relative w-full h-full max-w-7xl max-h-full bg-transparent flex flex-col"
          onClick={handleContentClick}
        >
          {/* Header */}
          <div className="relative z-10 flex items-center justify-between p-4 sm:p-6">
            <div className="flex items-center space-x-2 text-white/90">
              <div className="hidden sm:block text-sm font-medium">
                Image Preview
              </div>
              {scale !== 1 && (
                <div className="bg-white/10 backdrop-blur-md text-white/90 text-xs px-2 py-1 rounded-full border border-white/20">
                  {Math.round(scale * 100)}%
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Desktop controls */}
              <div className="hidden sm:flex items-center space-x-2">
                <button
                  onClick={handleZoomOut}
                  className="p-2 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-all duration-200 border border-white/20"
                  title="Zoom Out"
                  type="button"
                >
                  <ZoomOut className="h-5 w-5" />
                </button>
                <button
                  onClick={handleZoomIn}
                  className="p-2 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-all duration-200 border border-white/20"
                  title="Zoom In"
                  type="button"
                >
                  <ZoomIn className="h-5 w-5" />
                </button>
                <button
                  onClick={handleReset}
                  className="p-2 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-all duration-200 border border-white/20"
                  title="Reset View"
                  type="button"
                >
                  <RotateCcw className="h-5 w-5" />
                </button>
                <div className="w-px h-6 bg-white/20 mx-1"></div>
                <button
                  onClick={handleDownload}
                  className="p-2 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-all duration-200 border border-white/20"
                  title="Download"
                  type="button"
                >
                  <Download className="h-5 w-5" />
                </button>
                <button
                  onClick={handleShare}
                  className="p-2 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-all duration-200 border border-white/20"
                  title="Share"
                  type="button"
                >
                  <Share2 className="h-5 w-5" />
                </button>
              </div>
              
              {/* Mobile controls - simplified */}
              <div className="flex sm:hidden items-center space-x-2">
                <button
                  onClick={handleReset}
                  className="p-2 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-all duration-200 border border-white/20"
                  title="Reset View"
                  type="button"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
                <button
                  onClick={handleDownload}
                  className="p-2 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-all duration-200 border border-white/20"
                  title="Download"
                  type="button"
                >
                  <Download className="h-4 w-4" />
                </button>
              </div>
              
              <div className="w-px h-6 bg-white/20 mx-1"></div>
              <button
                onClick={handleClose}
                className="p-2 bg-red-500/20 backdrop-blur-md rounded-full text-white hover:bg-red-500/30 transition-all duration-200 border border-red-500/30"
                title="Close"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Image container */}
          <div className="flex-1 flex items-center justify-center overflow-hidden">
            <div 
              className="relative w-full h-full flex items-center justify-center touch-none"
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerEnd}
              onPointerLeave={handlePointerEnd}
              onClick={handleContentClick}
            >
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                </div>
              )}
              
              {imageError ? (
                <div className="text-white/70 text-center" onClick={handleContentClick}>
                  <div className="text-6xl mb-4">📷</div>
                  <div className="text-lg mb-2">Failed to load image</div>
                  <div className="text-sm opacity-70">The image could not be displayed</div>
                </div>
              ) : (
                <img
                  src={displayUrl}
                  alt="Preview"
                  className={`max-w-none transition-all duration-300 select-none rounded-lg shadow-2xl ${
                    scale > 1 
                      ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') 
                      : 'cursor-zoom-in'
                  } ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                  style={{
                    transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                    maxHeight: scale === 1 ? '90vh' : 'none',
                    maxWidth: scale === 1 ? '90vw' : 'none',
                    objectFit: 'contain'
                  }}
                  onClick={handleContentClick}
                  onDoubleClick={handleImageDoubleClick}
                  onPointerDown={handlePointerStart}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  draggable={false}
                />
              )}
            </div>
          </div>

          {/* Footer with instructions */}
          <div className="relative z-10 p-4 flex justify-center" onClick={handleContentClick}>
            <div className="bg-white/10 backdrop-blur-md text-white/90 text-sm px-4 py-2 sm:px-6 sm:py-3 rounded-full border border-white/20 pointer-events-none">
              <span className="hidden sm:inline">Double-click to zoom • Drag to pan when zoomed • Pinch to zoom on mobile</span>
              <span className="sm:hidden">Double-tap to zoom • Drag to pan • Pinch to zoom</span>
            </div>
          </div>

          {/* Mobile zoom controls overlay */}
          <div className="sm:hidden absolute bottom-20 left-4 flex flex-col space-y-2">
            <button
              onClick={handleZoomIn}
              className="p-3 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-all duration-200 border border-white/20"
              title="Zoom In"
              type="button"
            >
              <ZoomIn className="h-5 w-5" />
            </button>
            <button
              onClick={handleZoomOut}
              className="p-3 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-all duration-200 border border-white/20"
              title="Zoom Out"
              type="button"
            >
              <ZoomOut className="h-5 w-5" />
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );

  // Render modal using portal to avoid parent interference
  return createPortal(modalContent, document.body);
}